import { gql, useQuery } from '@apollo/client';

import { toCurrency } from '../../../utils/formatting';

import type {
  AdminGetGlobalStatisticsQuery,
  AdminGetGlobalStatisticsQueryVariables,
} from './__generated__/GlobalStatistics.gql';

const ADMIN_GET_GLOBAL_STATISTICS = gql`
  query AdminGetGlobalStatistics($userId: String!) {
    adminGetLatestTransactionStatistics(userId: $userId) {
      id
      startDate
      endDate
      statsJson
    }
  }
`;

interface IGlobalStatisticsProps {
  userId: string;
}

const GlobalStatistics = ({ userId }: IGlobalStatisticsProps) => {
  const { data, loading, error } = useQuery<AdminGetGlobalStatisticsQuery, AdminGetGlobalStatisticsQueryVariables>(
    ADMIN_GET_GLOBAL_STATISTICS,
    {
      variables: { userId },
      skip: !userId,
    }
  );

  if (loading) {
    return null;
  }

  if (error) {
    return null;
  }

  const globalStatsData = data?.adminGetLatestTransactionStatistics;

  const globalStats = globalStatsData?.statsJson as
    | {
        count: number;
        median: number;
        percentile75: number;
        percentile90: number;
        percentile95: number;
        percentile99: number;
      }
    | null
    | undefined;

  if (!globalStats) {
    return null;
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-5 text-lg font-semibold text-gray-900">Global statistics</div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <div className="flex flex-col gap-1">
          <span className="text-sm text-gray-600">Count</span>
          <span className="text-lg font-medium">{globalStats.count}</span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-sm text-gray-600">Median</span>
          <span className="text-lg font-medium">{toCurrency(globalStats.median)}</span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-sm text-gray-600">75th Percentile</span>
          <span className="text-lg font-medium">{toCurrency(globalStats.percentile75)}</span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-sm text-gray-600">90th Percentile</span>
          <span className="text-lg font-medium">{toCurrency(globalStats.percentile90)}</span>
        </div>
      </div>
      <div className="mt-6 border-t border-gray-200 pt-6">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">95th Percentile</span>
            <span className="text-lg font-medium">{toCurrency(globalStats.percentile95)}</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">99th Percentile</span>
            <span className="text-lg font-medium">{toCurrency(globalStats.percentile99)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalStatistics;
